import React from "react";
import {
  Box,
  Button,
  List,
  Typography,
  ListItem,
  ListItemText,
  Divider
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import CircleIcon from "@mui/icons-material/Brightness1";
import OutlinedCircleIcon from "@mui/icons-material/Brightness1Outlined";
import Modal from "../../components/Modal/Modal";
import ProgressBar from "../../components/ProgressBar/ProgressBar";
import { assignmentStatuses } from "../../constants";
import Skeleton from "../../components/Skeleton/Skeleton";

const styles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2)
  },
  list: {
    width: "100%"
  },
  divider: {
    marginLeft: theme.spacing(7),
    [theme.breakpoints.down("sm")]: {
      marginLeft: theme.spacing(6)
    }
  },
  listItemSectionOne: {
    display: "flex",
    alignItems: "center"
  },
  listItemSectionTwo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexBasis: theme.spacing(15.5),
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      flexBasis: theme.spacing(13),
      paddingLeft: theme.spacing(1)
    }
  },
  listItem: {
    color: theme.palette.text.secondary,
    minHeight: 48,
    display: "flex",
    justifyContent: "space-between"
  },
  listItemText: {
    textAlign: "end"
  },
  title: {
    marginLeft: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      marginLeft: theme.spacing(2.5)
    }
  }
}));

const CourseProgressSkeleton = () => {
  const listSize = 4;
  return (
    <Box
      display="flex"
      flexDirection="column"
      data-qa="course-progress-modal-skeleton"
    >
      <Skeleton height={20} width="80%" mb={2} />
      <Skeleton height={24} mb={2} />
      {[...Array(listSize)].map((v, index) => (
        <Box display="flex" key={index} pt={2}>
          <Skeleton variant="circular" height={24} width={24} mr={4} />
          <Box display="flex" flexDirection="column" flex={1}>
            <Box display="flex" justifyContent="space-between">
              <Skeleton
                height={20}
                width={`${70 + (index % 2) * 15}%`}
                mb={2}
              />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const ActivityTitle = ({ activityComplete, title }) => {
  const classes = styles();
  return (
    <Typography
      className={classes.title}
      variant="body2"
      color={activityComplete ? "textPrimary" : "textSecondary"}
    >
      {title}
    </Typography>
  );
};

const CompletionStatusIcon = ({ activityStatus }) => {
  const status = activityStatus === assignmentStatuses.completed;
  return status ? (
    <CircleIcon color="primary" />
  ) : (
    <OutlinedCircleIcon color="disabled" />
  );
};

const ActivityProgress = ({ status, completedScore }) => {
  const classes = styles();
  const { t } = useTranslation();
  const isComleted = status === assignmentStatuses.completed;
  if (isComleted) {
    if (completedScore || completedScore === 0) {
      return (
        <ListItemText
          className={classes.listItemText}
          primaryTypographyProps={{
            className: "italic",
            variant: "body2",
            color: "textPrimary"
          }}
        >
          {completedScore}%
        </ListItemText>
      );
    } else {
      return (
        <ListItemText
          className={classes.listItemText}
          primaryTypographyProps={{
            className: "italic",
            variant: "body2",
            color: "textPrimary"
          }}
        >
          {t("coursePlayer.activityStatus.completed")}
        </ListItemText>
      );
    }
  } else {
    return (
      <ListItemText
        className={classes.listItemText}
        primaryTypographyProps={{
          className: "italic",
          variant: "body2",
          color: "textSecondary"
        }}
      >
        {status?.toLowerCase() === assignmentStatuses.inProgress.toLowerCase()
          ? t("coursePlayer.activityStatus.inProgress")
          : t("coursePlayer.activityStatus.notStarted")}
      </ListItemText>
    );
  }
};

const CourseProgressModal = ({
  setModalOpen,
  open,
  trainingName,
  loading,
  currentProgress,
  completedActivities,
  totalCompletion
}) => {
  const classes = styles();
  const { t } = useTranslation();

  const closeModal = e => {
    setModalOpen(false);
    e.stopPropagation();
  };

  const assignmentActivities = !!currentProgress
    ? currentProgress.map((activity, index) => (
        <div key={activity.id}>
          <ListItem
            disableGutters
            key={index}
            classes={{ root: classes.listItem }}
          >
            <div className={classes.listItemSectionOne}>
              <CompletionStatusIcon activityStatus={activity.status} />
              <ActivityTitle
                activityComplete={
                  activity.status === assignmentStatuses.completed
                }
                title={activity.title}
              />
            </div>
            <div className={classes.listItemSectionTwo}>
              <ActivityProgress
                status={activity.status}
                completedScore={activity.completedScore}
              />
            </div>
          </ListItem>
          {index !== currentProgress?.length - 1 && (
            <Divider classes={{ root: classes.divider }} />
          )}
        </div>
      ))
    : null;

  return (
    <Modal
      disableEnforceFocus
      showCloseIcon
      maxWidth="sm"
      fullWidth
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {"Course Progress: " + trainingName}
        </Typography>
      }
      dialogContent={
        loading ? (
          <CourseProgressSkeleton />
        ) : (
          <Box display="flex" flexDirection="column" mb={1}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" noWrap sx={{ pr: 3 }}>
                Total Completion
              </Typography>
              <Typography variant="h6">{totalCompletion}%</Typography>
            </Box>
            <ProgressBar
              totalSteps={assignmentActivities?.length || 0}
              completedSteps={completedActivities}
              color="primary"
              height={24}
            />
            <Box mt={3}>
              <List aria-label="Assignment Activity List">
                {assignmentActivities}
              </List>
            </Box>
          </Box>
        )
      }
      dialogActions={
        <>
          <Button color="primary" onClick={e => closeModal(e)}>
            {t("closeBtn")}
          </Button>
        </>
      }
      data-qa="view-progress-modal"
      open={open}
      onClose={e => closeModal(e)}
    />
  );
};

export default CourseProgressModal;
